'use client';
import { isEqual } from 'lodash';
import { usePathname, useRouter } from 'next/navigation';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactElement,
  ReactNode,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { IIdleTimer, useIdleTimer } from 'react-idle-timer';
import { useAnalytics } from '@provider-directory/components/analytics/analytics-provider';
import { InactivityModal } from '@provider-directory/components/modals';
import { useOrgContext } from '@provider-directory/components/org-context/org-context-provider';
import {
  DEFAULT_APPOINTMENT,
  useAppointment,
} from '@provider-directory/contexts/AppointmentContext';

export type InactivityModalState =
  | 'closed'
  | 'warning'
  | 'warning-final'
  | 'expired';

export interface InactivityModalContent {
  titleKey: string;
  bodyKey: string;
  buttonLabelKey: string;
  buttonAction: 'extend' | 'dismiss' | 'reload';
}

const InactivityContext = createContext<IIdleTimer | null>(null);
const DEFAULT_TIMEOUT = 30 * 60 * 1000;
const DEFAULT_DEBOUNCE_DELAY = 250;
const CLEAR_APPOINTMENT_DELAY = 250;

const WARNING_BEFORE_IDLE = 60 * 1000;
const MAX_EXTENSIONS = 10;
const ROUTES_SCHEDULE_FLOW = '/providers/appointment/request';

export const InactivityProvider = ({
  children,
}: {
  children: ReactNode;
}): ReactElement => {
  const [modalState, setModalState] = useState<InactivityModalState>('closed');
  const [extensionCount, setExtensionCount] = useState(0);
  const { getProvidersUrl } = useOrgContext();
  const { resetAppointment, appointment } = useAppointment();
  const analytics = useAnalytics();
  const pathname = usePathname();
  const router = useRouter();
  const isMountedRef = useRef(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const extensionCountRef = useRef(extensionCount);
  const pathnameRef = useRef(pathname);
  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  const clearAppointmentWithDelay = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        resetAppointment();
      }
    }, CLEAR_APPOINTMENT_DELAY);
  }, [resetAppointment]);

  const onPrompt = useCallback(() => {
    if (extensionCountRef.current >= MAX_EXTENSIONS) {
      setModalState('warning-final');
    } else {
      setModalState('warning');
    }
  }, []);

  const onIdle = useCallback(() => {
    analytics.track('TIMED_OUT', {
      pathname: pathnameRef.current,
      extensionsUsed: extensionCountRef.current,
    });
    setModalState('expired');
  }, [analytics]);

  const isIdleRoute = pathname.includes(ROUTES_SCHEDULE_FLOW);
  const isAppointmentValid = !isEqual(appointment, DEFAULT_APPOINTMENT);
  const idleTimerProps = useIdleTimer({
    timeout: DEFAULT_TIMEOUT,
    debounce: DEFAULT_DEBOUNCE_DELAY,
    promptBeforeIdle: WARNING_BEFORE_IDLE,
    onPrompt,
    onIdle,
    startManually: true,
  });

  const { start, pause, isIdle, activate, getRemainingTime } = idleTimerProps;

  const contentForState = useMemo((): InactivityModalContent => {
    if (modalState === 'expired') {
      return {
        titleKey: 'inactivity.expired.title',
        bodyKey: 'inactivity.expired.body',
        buttonLabelKey: 'inactivity.expired.reload',
        buttonAction: 'reload',
      };
    }
    if (modalState === 'warning-final') {
      return {
        titleKey: 'inactivity.warning.title',
        bodyKey: 'inactivity.warningFinal.body',
        buttonLabelKey: 'inactivity.warningFinal.dismiss',
        buttonAction: 'dismiss',
      };
    }
    return {
      titleKey: 'inactivity.warning.title',
      bodyKey: 'inactivity.warning.body',
      buttonLabelKey: 'inactivity.warning.extend',
      buttonAction: 'extend',
    };
  }, [modalState]);

  const remainingExtensions = MAX_EXTENSIONS - extensionCount;
  const lastContentRef = useRef<InactivityModalContent>(contentForState);
  const lastRemainingExtensionsRef = useRef(remainingExtensions);

  useEffect(() => {
    if (modalState !== 'closed') {
      lastContentRef.current = contentForState;
      lastRemainingExtensionsRef.current = remainingExtensions;
    }
  }, [modalState, contentForState, remainingExtensions]);

  // Use frozen values so the modal doesn't flicker during close animation
  const modalContent =
    modalState !== 'closed' ? contentForState : lastContentRef.current;
  const modalRemainingExtensions =
    modalState !== 'closed'
      ? remainingExtensions
      : lastRemainingExtensionsRef.current;

  const onExtendSession = useCallback(() => {
    const newCount = extensionCountRef.current + 1;
    extensionCountRef.current = newCount;
    setExtensionCount(newCount);
    setModalState('closed');
    activate();
    analytics.track('SESSION_EXTENDED', {
      pathname: pathnameRef.current,
      extensionNumber: newCount,
    });
  }, [activate, analytics]);

  const onDismiss = useCallback(() => {
    setModalState('closed');
  }, []);

  const onReloadHandler = useCallback(() => {
    setModalState('closed');
    if (isIdleRoute) {
      router.replace(getProvidersUrl());
      clearAppointmentWithDelay();
      return;
    }
    window.location.reload();
    clearAppointmentWithDelay();
  }, [isIdleRoute, getProvidersUrl, router, clearAppointmentWithDelay]);

  useEffect(() => {
    if (isIdleRoute && isAppointmentValid && !isIdle()) {
      start();
      return;
    }
    if (!isAppointmentValid) {
      pause();
      setModalState('closed');
      setExtensionCount(0);
      extensionCountRef.current = 0;
    }
  }, [isIdleRoute, isAppointmentValid, isIdle, start, pause]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <InactivityContext.Provider value={idleTimerProps}>
      <InactivityModal
        isOpen={modalState !== 'closed'}
        modalState={modalState}
        content={modalContent}
        onExtendSession={onExtendSession}
        onDismiss={onDismiss}
        onReload={onReloadHandler}
        remainingExtensions={modalRemainingExtensions}
        getRemainingTime={getRemainingTime}
      />
      {children}
    </InactivityContext.Provider>
  );
};

export const useInactivity = (): IIdleTimer => {
  const context = useContext(InactivityContext);
  if (!context) {
    throw new Error('useInactivity must be used within an InactivityProvider');
  }
  return context;
};
